import { useState, useEffect, useCallback, useRef } from 'react';
import SwiperType from 'swiper';

interface UseSwipeNavigationProps {
  swiperRef?: SwiperType;
  totalSlides?: number;
  isLoopMode?: boolean;
}

interface SwipeNavigationState {
  showPrevArrow: boolean;
  showNextArrow: boolean;
}

/**
 * Pixel threshold to prevent arrow flickering at slider boundaries.
 * Small buffer accounts for subpixel rendering and floating-point precision
 * when comparing scroll position to content boundaries.
 */
const TRANSLATE_THRESHOLD = 5;

/**
 * Debounce delay (ms) for arrow visibility calculations.
 * Prevents excessive re-renders during rapid swiper state changes (initialization, resize).
 */
const VISIBILITY_DEBOUNCE_MS = 16; // ~1 frame (60fps) as 16ms = 62.5 fps

/**
 * Custom hook for managing navigation arrow visibility in Swiper sliders.
 *
 * Intelligently shows/hides prev/next arrows based on:
 * - Content overflow (whether slides extend beyond visible area)
 * - Current scroll position (at beginning, middle, or end)
 * - Loop mode (always show arrows when looping is enabled)
 * - Responsive updates on window resize and orientation changes
 *
 * Features:
 * - Automatically calculates total content width including margins
 * - Hides arrows at boundaries (start/end) in non-loop mode
 * - Shows both arrows in loop mode with multiple slides
 * - Handles ResizeObserver and Swiper events for real-time updates
 *
 * @param {UseSwipeNavigationProps} props - Configuration options
 * @param {SwiperType} props.swiperRef - Swiper instance reference
 * @param {number} props.totalSlides - Total number of slides
 * @param {boolean} props.isLoopMode - Whether slider is in loop mode
 *
 * @returns {SwipeNavigationState} Visibility state for prev/next arrows
 *
 * @example
 * ```tsx
 * const { showPrevArrow, showNextArrow } = useSwipeNavigation({
 *   swiperRef: swiperInstance,
 *   totalSlides: images.length,
 *   isLoopMode: true
 * });
 *
 * {showPrevArrow && <button onClick={...}>Previous</button>}
 * {showNextArrow && <button onClick={...}>Next</button>}
 * ```
 */
export const useSwipeNavigation = ({ swiperRef, totalSlides, isLoopMode }: UseSwipeNavigationProps): SwipeNavigationState => {
  const [arrowState, setArrowState] = useState({ showPrevArrow: false, showNextArrow: true });
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Core calculation logic - updates state in a single batch to minimize re-renders
  const calculateArrowVisibilityImmediate = useCallback(() => {
    if (!swiperRef) return;

    // In loop mode with multiple slides, always show both arrows
    if (isLoopMode && totalSlides && totalSlides > 1) {
      setArrowState({ showPrevArrow: true, showNextArrow: true });
      return;
    }

    const swiperWidth = swiperRef.width;
    const slides = swiperRef.slides;

    if (!slides || slides.length === 0) {
      setArrowState({ showPrevArrow: false, showNextArrow: false });
      return;
    }

    const slidesArray = Array.isArray(slides) ? (slides as HTMLElement[]) : (Array.from(slides || []) as HTMLElement[]);

    // Get spaceBetween from Swiper params (defaults to 0 if not set)
    const spaceBetween = (swiperRef.params?.spaceBetween as number) || 0;

    const totalContentWidth: number = slidesArray.reduce((total: number, slide: HTMLElement, index: number) => {
      const slideWidth = slide.offsetWidth || 0;
      const marginRight = index < slidesArray.length - 1 ? spaceBetween : 0;
      return total + slideWidth + marginRight;
    }, 0);

    // Check if content overflows and current position
    const hasOverflow = totalContentWidth > swiperWidth;
    const translateX = swiperRef.translate || 0;
    const maxTranslate = totalContentWidth - swiperWidth;

    const shouldShowPrev = hasOverflow && Math.abs(translateX) > TRANSLATE_THRESHOLD;
    const shouldShowNext = hasOverflow && Math.abs(translateX) < maxTranslate - TRANSLATE_THRESHOLD;

    setArrowState({ showPrevArrow: shouldShowPrev, showNextArrow: shouldShowNext });
  }, [swiperRef, totalSlides, isLoopMode]);

  // Debounced version to prevent excessive re-renders during rapid state changes
  const calculateArrowVisibility = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      calculateArrowVisibilityImmediate();
      debounceTimeoutRef.current = null;
    }, VISIBILITY_DEBOUNCE_MS);
  }, [calculateArrowVisibilityImmediate]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleResize = useCallback(() => {
    if (!swiperRef) return;

    swiperRef.update();
    swiperRef.updateSlides();
    swiperRef.updateProgress();
    swiperRef.updateSlidesClasses();
    calculateArrowVisibility();
  }, [swiperRef, calculateArrowVisibility]);

  useEffect(() => {
    if (!swiperRef || !swiperRef.slides) {
      setArrowState({ showPrevArrow: false, showNextArrow: true });
      return () => {};
    }

    // Capture swiperRef at subscription time to ensure cleanup targets the correct instance.
    // This prevents memory leaks if swiperRef becomes null/undefined before cleanup runs.
    const swiper = swiperRef;

    // Initial calculation - use immediate version for first render to avoid flash
    calculateArrowVisibilityImmediate();

    const handleReachEnd = () => {
      if (!isLoopMode) setArrowState((prev) => ({ ...prev, showNextArrow: false }));
    };
    const handleReachBeginning = () => {
      if (!isLoopMode) setArrowState((prev) => ({ ...prev, showPrevArrow: false }));
    };

    window.addEventListener('resize', handleResize);
    swiper.on('slideChange', calculateArrowVisibility);
    swiper.on('reachEnd', handleReachEnd);
    swiper.on('reachBeginning', handleReachBeginning);
    swiper.on('fromEdge', calculateArrowVisibility);
    swiper.on('init', calculateArrowVisibility);
    swiper.on('resize', calculateArrowVisibility);
    swiper.on('orientationchange', calculateArrowVisibility);

    return () => {
      window.removeEventListener('resize', handleResize);
      swiper.off('slideChange', calculateArrowVisibility);
      swiper.off('reachEnd', handleReachEnd);
      swiper.off('reachBeginning', handleReachBeginning);
      swiper.off('fromEdge', calculateArrowVisibility);
      swiper.off('init', calculateArrowVisibility);
      swiper.off('resize', calculateArrowVisibility);
      swiper.off('orientationchange', calculateArrowVisibility);
    };
  }, [swiperRef, isLoopMode, calculateArrowVisibility, calculateArrowVisibilityImmediate, handleResize]);

  return arrowState;
};
